// Layer 3 (Transformation): the improvement pipeline — four lanes, one per
// stage in the seed's union, counted from the register.
//
// The lane board *is* the funnel: there is no second set of typed counts
// beside it, because two counts of the same rows drift apart. Every figure
// here comes from `improvementRegister().rows`, so a row added to the seed
// lands in exactly one lane and moves every count with it.

import { improvementRegister, improvementStageTone, type ImprovementRow } from './Scene.transform';
import type { ImprovementStage } from './seed';
import type { VizTone } from '@/versions/v1/modules/_viz/viz-tone';

export const PIPELINE_STAGES: readonly ImprovementStage[] = [
  'candidate',
  'priced',
  'capital request',
  'approved',
];

export type PipelineCard = {
  id: string;
  opportunity: string;
  /** Unpriced rows carry `—` — they have no benefit term to sum. */
  benefitPerYear: string;
  capex: string;
  paybackYears: string;
  stage: ImprovementStage;
};

export type PipelineLane = {
  stage: ImprovementStage;
  label: string;
  tone: VizTone;
  count: number;
  rows: readonly PipelineCard[];
};

export type PipelineView = {
  lanes: readonly PipelineLane[];
  total: number;
};

export const STAGE_LABEL: Record<ImprovementStage, string> = {
  candidate: 'Candidate',
  priced: 'Priced',
  'capital request': 'Capital request',
  approved: 'Approved',
};

export function improvementPipeline(): PipelineView {
  const { rows } = improvementRegister();
  const lanes = PIPELINE_STAGES.map((stage) => {
    const laneRows = rows.filter((row) => row.stage === stage);
    return {
      stage,
      label: STAGE_LABEL[stage],
      tone: improvementStageTone(stage),
      count: laneRows.length,
      rows: laneRows.map((row: ImprovementRow) => ({
        id: row.id,
        opportunity: row.opportunity,
        benefitPerYear: row.benefitPerYear,
        capex: row.capex,
        paybackYears: row.paybackYears,
        stage: row.stage,
      })),
    };
  });

  return { lanes, total: rows.length };
}

export function improvementStageLabel(stage: ImprovementStage): string {
  return STAGE_LABEL[stage];
}

/** The pipeline as a four-cell strip (polish.P7, the Advisory strip object):
 *  each stage's count and its share of the register. */
export type PipelineStripCell = { count: string; comparisonLead: string; comparison: string };

export function improvementPipelineStrip(): Record<ImprovementStage, PipelineStripCell> {
  const view = improvementPipeline();
  return Object.fromEntries(
    view.lanes.map((lane) => [
      lane.stage,
      {
        count: String(lane.count),
        comparisonLead: `${view.total > 0 ? Math.round((lane.count / view.total) * 100) : 0}%`,
        comparison: `of ${view.total} in the register`,
      },
    ]),
  ) as Record<ImprovementStage, PipelineStripCell>;
}
